import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { MedicalItem } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";

import img_icon_mild from "@/Globals/_images/icons/painscale/mild.png";
import img_icon_moderate from "@/Globals/_images/icons/painscale/Moderate.png";
import img_icon_no from "@/Globals/_images/icons/painscale/No.png";
import img_icon_severe2 from "@/Globals/_images/icons/painscale/Severe2.png";
import img_icon_verysevere from "@/Globals/_images/icons/painscale/VerySevere.png";
import img_icon_worstpain from "@/Globals/_images/icons/painscale/WorstPain.png";

interface IVitalSignSummaryProps {
    vSign: MedicalItemVitalSign;
    dateOfBirth: Date;
    encounterId: string;
    vitalSignPercentileUtility: VitalSignPercentileUtility;
    onMedicalItemClick: (item: MedicalItem, view: MedicalItemsTab) => void;
    addVitalSign: () => void;
}

function VitalSignSummary(props: IVitalSignSummaryProps): JSX.Element {
    const renderPercentile = (property: VitalProperty, vitalSign: MedicalItemVitalSign = null): React.ReactNode => {
        const percentile = props.vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, vitalSign);
        const percentileString = props.vitalSignPercentileUtility?.getPercentileText(property, { [property]: percentile });
        return percentile !== null ? <span className="text-italic">{percentileString}</span> : null;
    };

    const result: JSX.Element[] = [];

    const ageDifference = props.vitalSignPercentileUtility?.getAgeFromPatientDateOfBirthAndPharmaStartDateDifference(
        props.dateOfBirth,
        props.vSign.PharmaStartDate
    );

    const headCircumferenceAgeLimit = 5;

    if (!props.vSign)
        return null;

    const invalidStyle = props.vSign.DisplayAsInvalid ? "invalid-medical-item" : "";

    const date = props.vSign.PharmaStartDate != null ? props.vSign.PharmaStartDate : props.vSign.CommitDate;

    { props.vSign.Temperature &&
        result.push(
            <div key="vitals_temp" className="note-items-list">
                <span> <strong> {cacheStore.GetString("TemperatureDLabel")} </strong> </span>
                <a
                    className="vital-sign-item"
                    onClick={() => {
                        if (props.vSign.RelatedEncounterId == props.encounterId)
                            props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns);
                        else
                            props.addVitalSign();
                    }}
                >
                    <span className={invalidStyle}>
                        {Number(props.vSign.Temperature.toFixed(2)).toString().trim()} {cacheStore.GetLocalizedValueByLookupId(props.vSign.TemperatureUnitLookupId)}
                    </span>
                </a>
            </div>
        );
    }

    if (props.vSign.PainScaleValue) {
        let img = null;
        let severityString = "";

        const imgStyle: React.CSSProperties = { width: "16px", height: "16px", margin: "0px 4px 4px 4px" };

        switch (props.vSign.PainScaleValue) {
            case props.vSign.PainScaleValue = 0: case props.vSign.PainScaleValue = 1:
                img = (<img style={imgStyle} src={img_icon_no} />);
                severityString = cacheStore.GetString("NoneLabel");
                break;

            case props.vSign.PainScaleValue = 2: case props.vSign.PainScaleValue = 3:
                img = (<img style={imgStyle} src={img_icon_mild} />);
                severityString = cacheStore.GetString("MildLabel");
                break;

            case props.vSign.PainScaleValue = 4: case props.vSign.PainScaleValue = 5:
                img = (<img style={imgStyle} src={img_icon_moderate} />);
                severityString = cacheStore.GetString("ModerateLabel");
                break;

            case props.vSign.PainScaleValue = 6: case props.vSign.PainScaleValue = 7:
                img = (<img style={imgStyle} src={img_icon_severe2} />);
                severityString = cacheStore.GetString("SevereLabel");
                break;

            case props.vSign.PainScaleValue = 8: case props.vSign.PainScaleValue = 9:
                img = (<img style={imgStyle} src={img_icon_verysevere} />);
                severityString = cacheStore.GetString("VerySevereLabel");
                break;

            case props.vSign.PainScaleValue = 10: case props.vSign.PainScaleValue = 11:
                img = (<img style={imgStyle} src={img_icon_worstpain} />);
                severityString = cacheStore.GetString("WorstPainLabel");
                break;
        }
        result.push(
            <div key="vitals_pain" className="note-items-list">
                <span><strong>{cacheStore.GetString("PainDLabel")} </strong></span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    {img}
                    <span className={invalidStyle}>
                        {props.vSign.PainScaleValue} - {severityString}
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.BPSys && props.vSign.BPDia &&
        result.push(
            <div key="vitals_bp" className="note-items-list">
                <span> <strong> {cacheStore.GetString("BPDLabel")} </strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.BPSys.toFixed(2)).toString().trim()} / {Number(props.vSign.BPDia.toFixed(2)).toString().trim()}
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.BPSysStanding &&
        result.push(
            <div key="vitals_bpstanding" className="note-items-list">
                <span> <strong> {cacheStore.GetString("BPStandingDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.BPSysStanding.toFixed(2)).toString().trim()} /  / {MyleHelper.ReplaceEmptyString(props.vSign.BPDiaStanding ? props.vSign.BPDiaStanding.toString() : "")}
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.BPSysLaid &&
        result.push(
            <div key="vitals_bpLaid" className="note-items-list">
                <span> <strong> {cacheStore.GetString("BPLayingDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.BPSysLaid.toFixed(2)).toString().trim()}  / {MyleHelper.ReplaceEmptyString(props.vSign.BPDiaLaid ? props.vSign.BPDiaLaid.toString() : "")}
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.HeartRate &&
        result.push(
            <div key="vitals_hr" className="note-items-list">
                <span> <strong> {cacheStore.GetString("HRDLabel")} </strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.HeartRate.toFixed(0)).toString().trim()} bpm
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.O2Sat &&
        result.push(
            <div key="vitals_o2sat" className="note-items-list">
                <span><strong>O<sub>2</sub>Sat:</strong></span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.O2Sat.toFixed(2)).toString().trim()} %
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.RespiratoryRate &&
        result.push(
            <div key="vitals_rr" className="note-items-list">
                <span> <strong>{cacheStore.GetString("RespirationRateDAbbrevLabel")}  </strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.RespiratoryRate.toFixed(0)).toString().trim()} / min
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.Weight &&
        result.push(
            <div key="vitals_weight" className="note-items-list">
                <span> <strong> {cacheStore.GetString("WeightDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.Weight.toFixed(3)).toString().trim()} {cacheStore.GetLocalizedValueByLookupId(props.vSign.WeightUnitLookupId)}
                    </span>
                </a>
                <span className="percentile">{renderPercentile("Weight", props.vSign)}</span>
            </div>
        );
    }

    { props.vSign.Height &&
        result.push(
            <div key="vitals_height" className="note-items-list">
                <span> <strong> {cacheStore.GetString("HeightDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.Height.toFixed(3)).toString().trim()} {cacheStore.GetLocalizedValueByLookupId(props.vSign.HeightUnitLookupId)}
                    </span>
                </a>
                <span className="percentile">{renderPercentile("Height", props.vSign)}</span>
            </div>
        );
    }

    { props.vSign.MassBodyIndex &&
        result.push(
            <div key="vitals_bmi" className="note-items-list">
                <span> <strong> {cacheStore.GetString("BMIDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.MassBodyIndex.toFixed(2)).toString().trim()}
                    </span>
                </a>
                <span className="percentile">{renderPercentile("MassBodyIndex", props.vSign)}</span>
            </div>
        );
    }

    { props.vSign.WaistCircumference &&
        result.push(
            <div key="vitals_waistCird" className="note-items-list">
                <span> <strong> {cacheStore.GetString("WaistCircumferenceDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.WaistCircumference.toFixed(2)).toString().trim()} {cacheStore.GetLocalizedValueByLookupId(props.vSign.WaistCircumferenceUnitLookupId)}
                    </span>
                </a>
            </div>
        );
    }

    { ageDifference <= headCircumferenceAgeLimit && props.vSign.HeadCircumference &&
        result.push(
            <div key="vitals_hc" className="note-items-list">
                <span>
                    <strong>
                        {cacheStore.GetString("HeadCircumferenceAbbrev")}
                    </strong>
                </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.HeadCircumference.toFixed(2)).toString().trim()}
                    </span>
                </a>
                <span className="percentile">{renderPercentile("HeadCircumference", props.vSign)}</span>
            </div>
        );
    }

    { props.vSign.CreatinineClearance &&
        result.push(
            <div key="vitals_crcl" className="note-items-list">
                <span> <strong> {cacheStore.GetString("CreaClDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {props.vSign.CreatinineClearance.toFixed(2).toString()} {props.vSign.CreatinineClearance ? <span>ml/min/1.73m<sup>2</sup></span> : ""}
                    </span>
                </a>
            </div>
        );
    }

    { props.vSign.BloodGlucose &&
        result.push(
            <div key="vitals_bgl" className="note-items-list">
                <span> <strong> {cacheStore.GetString("BloodGlucoseDLabel")}</strong> </span>
                <a className="vital-sign-item" onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                    <span className={invalidStyle}>
                        {Number(props.vSign.BloodGlucose.toFixed(2)).toString().trim()} {props.vSign.BloodGlucose ? cacheStore.GetLocalizedValueByLookupId(props.vSign.BloodGlucoseUnitLookupId) : ""}
                    </span>
                </a>
            </div>
        );
    }

    result.push(
        <div key="vitals_dt" className="note-items-list">
            <span onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                <strong>
                    {LuxonDateTimeParser.parse(date).toFormat("yyyy-MM-dd HH:mm:ss")}
                </strong>
            </span>
        </div>
    );

    { !IsNullOrWhiteSpace(props.vSign.Note) &&
        result.push(
            <div key="vitals_note" className="pointer margin-lg-left text-italic" style={{ fontSize: "11px", fontStyle: "italic" }} onClick={() => props.onMedicalItemClick(props.vSign, MedicalItemsTab.VitalSigns)}>
                <span className={invalidStyle}>
                    {JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(props.vSign.Note))}
                </span>
            </div>
        );
    }
    return <>{result}</>;
}

export { VitalSignSummary };
